import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useProjectAssignments } from '@/hooks/useProjectAssignments';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Search } from 'lucide-react';
import type { Project } from '@/types/crm';

interface ProjectAssignmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portalUser: {
    id: string;
    email: string;
    role: string;
  };
}

export function ProjectAssignmentModal({ open, onOpenChange, portalUser }: ProjectAssignmentModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  
  const { assignments, isLoading: assignmentsLoading } = useProjectAssignments(portalUser.id);
  const { bulkAssign, isBulkAssigning } = useProjectAssignments(portalUser.id);

  const { data: allProjects, isLoading: projectsLoading } = useQuery({
    queryKey: ['all-projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .is('deleted_at', null)
        // T1.1 — hier stond .in('stage', ['intake','gewonnen','afgerond','onderhandeling']).
        // Na migratie 030 bestaat geen van die vier waarden nog, dus deze query
        // zou een lege lijst geven zonder ook maar een foutmelding: geen enkel
        // project meer toewijsbaar aan een portaalgebruiker.
        //
        // De oude lijst is niet één-op-één te vertalen. Hij bevatte 'intake'
        // maar niet 'analyse', en 'onderhandeling' maar niet 'voorstel' — en
        // die paren zijn nu allebei tot één stadium ingeklapt. Het onderscheid
        // dat hij maakte bestaat dus niet meer (en oogde sowieso onbedoeld).
        // Vervangen door de regel die wél uit te leggen is: alles behalve
        // verloren trajecten. Zie de losse bevinding in docs/ROADMAP-CRM.md.
        .neq('status', 'verloren')
        .order('name');

      if (error) throw error;
      return (data || []) as unknown as Project[];
    },
  });

  // Initialize selected projects with current assignments
  useEffect(() => {
    if (assignments) {
      const assignedProjectIds = new Set(assignments.map(a => a.project_id));
      setSelectedProjects(assignedProjectIds);
    }
  }, [assignments]);

  const filteredProjects = allProjects?.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.project_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.organization?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const toggleProject = (projectId: string) => {
    setSelectedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    const currentAssignments = new Set(assignments?.map(a => a.project_id) || []);
    const toAdd = Array.from(selectedProjects).filter(id => !currentAssignments.has(id));
    const toRemove = Array.from(currentAssignments).filter(id => !selectedProjects.has(id));

    // Add new assignments
    if (toAdd.length > 0) {
      bulkAssign({
        portalUserId: portalUser.id,
        projectIds: toAdd,
      });
    }

    // Remove old assignments
    for (const projectId of toRemove) {
      const assignment = assignments?.find(a => a.project_id === projectId);
      if (assignment) {
        await supabase
          .from('project_assignments')
          .delete()
          .eq('id', assignment.id);
      }
    }

    onOpenChange(false);
  };

  const selectAll = () => {
    setSelectedProjects(new Set(filteredProjects.map(p => p.id)));
  };

  const deselectAll = () => {
    setSelectedProjects(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Projecten beheren voor {portalUser.email}</DialogTitle>
          <DialogDescription>
            Selecteer de projecten waartoe deze {portalUser.role === 'inspector' ? 'inspector' : 'klant'} toegang heeft
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Zoek projecten..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Bulk Actions */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={selectAll}>
              Alles selecteren
            </Button>
            <Button variant="outline" size="sm" onClick={deselectAll}>
              Alles deselecteren
            </Button>
            <div className="ml-auto text-sm text-muted-foreground">
              {selectedProjects.size} van {allProjects?.length || 0} geselecteerd
            </div>
          </div>

          {/* Project List */}
          <ScrollArea className="h-[400px] border rounded-md p-4">
            {projectsLoading || assignmentsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Geen projecten gevonden
              </div>
            ) : (
              <div className="space-y-2">
                {filteredProjects.map(project => (
                  <div
                    key={project.id}
                    className="flex items-start gap-3 p-3 rounded-md hover:bg-muted cursor-pointer"
                    onClick={() => toggleProject(project.id)}
                  >
                    <Checkbox
                      checked={selectedProjects.has(project.id)}
                      onCheckedChange={() => toggleProject(project.id)}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{project.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {project.project_number && `#${project.project_number} • `}
                        {project.organization?.name || 'Geen organisatie'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuleren
          </Button>
          <Button onClick={handleSave} disabled={isBulkAssigning}>
            {isBulkAssigning ? 'Opslaan...' : 'Opslaan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
